import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";

interface UserPreferences {
  currency: string;
  number_format: string;
  date_format: string;
  timezone: string;
  theme: string;
  language: string;
  week_starts_on: string;
}

interface PreferencesContextType {
  preferences: UserPreferences;
  isLoading: boolean;
  refreshPreferences: () => Promise<void>;
}

const defaultPreferences: UserPreferences = {
  currency: 'INR',
  number_format: 'indian',
  date_format: 'DD/MM/YYYY',
  timezone: 'Asia/Kolkata',
  theme: 'light',
  language: 'en',
  week_starts_on: 'monday',
};

const PreferencesContext = createContext<PreferencesContextType>({
  preferences: defaultPreferences,
  isLoading: true,
  refreshPreferences: async () => {},
});

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error("usePreferences must be used within PreferencesProvider");
  }
  return context;
};

export const PreferencesProvider = ({ children }: { children: ReactNode }) => {
  const { session } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [isLoading, setIsLoading] = useState(false); // ⭐ Don't block rendering initially

  const fetchPreferences = async () => {
    if (!session?.user?.id) {
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", session.user.id)
        .single();

      if (error) {
        console.error("Error fetching preferences:", error);
        setPreferences(defaultPreferences);
      } else if (data) {
        setPreferences({
          currency: data.currency || defaultPreferences.currency,
          number_format: data.number_format || defaultPreferences.number_format,
          date_format: data.date_format || defaultPreferences.date_format,
          timezone: data.timezone || defaultPreferences.timezone,
          theme: data.theme || defaultPreferences.theme,
          language: data.language || defaultPreferences.language,
          week_starts_on: data.week_starts_on || defaultPreferences.week_starts_on,
        });
      }
    } catch (error) {
      console.error("Error in fetchPreferences:", error);
      setPreferences(defaultPreferences);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshPreferences = async () => {
    await fetchPreferences();
  };

  useEffect(() => {
    fetchPreferences();
  }, [session?.user?.id]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!session?.user?.id) return;

    const channel = supabase
      .channel("user_preferences_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user_preferences",
          filter: `user_id=eq.${session.user.id}`,
        },
        () => {
          fetchPreferences();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session?.user?.id]);

  return (
    <PreferencesContext.Provider value={{ preferences, isLoading, refreshPreferences }}>
      {children}
    </PreferencesContext.Provider>
  );
};
